/**
 * Storyblok Visual Editor preview — Phase 1a (raw fetch).
 *
 * When a content editor opens any story in the Storyblok dashboard, Storyblok
 * iframes this page with the story ID + a signature token in query params:
 *
 *   /storyblok/<short-slug>?_storyblok=<id>&_storyblok_c=<component>
 *     &_storyblok_tk[token]=<sig>&_storyblok_tk[space_id]=...
 *     &_storyblok_lang=default&_storyblok_release=0
 *
 * This component:
 *   1. Pulls the story ID from `_storyblok`
 *   2. Fetches the full story via Storyblok CDN (find_by=id, version=draft,
 *      with resolve_relations so categories + items come back inlined)
 *   3. Derives tenantSlug + contentType from story.full_slug
 *   4. Dumps the story JSON so we can validate the fetch end-to-end
 *
 * Real FAQ/Card/Agreement rendering replaces the JSON dump in Phase 1b.
 * Live editing (Storyblok bridge wiring) lands in Phase 1c.
 */

import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { Alert, Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { storyblokInit, apiPlugin, getStoryblokApi, useStoryblokState } from '@storyblok/react';

import { CompletionUiPreview, type CompletionUiBlok } from './CompletionUiPreview';
import { FaqPagePreview, FaqCategoryPreview, FaqQuestionPreview } from './FaqPreview';
import { CardPagePreview, CardCategoryPreview, ContentCardPreview } from './CardPreview';
import { ActionPagePreview, ActionCategoryPreview, ActionItemPreview } from './ActionPreview';
import { AgreementPagePreview, AgreementCategoryPreview, AgreementItemPreview } from './AgreementPreview';
import { ToolTipPreview, ToolTipContentBlok, InformationModalPreview, InformationModalContentBlok, BenefitsCardArtPreview, BenefitsCardArtContentBlok, CtaCardBannerPreview, CtaCardBannerContentBlok, ShopOnlineCarouselPreview, ShopOnlineCarouselContentBlok, OnboardingSplashPreview, OnboardingSplashContentBlok, PurseDetailsPreview, PurseDetailsContentBlok, CohortSpecificContentPreview, CohortSpecificContentBlok, UILanguageResourcePreview, UILanguageResourceContentBlok, TriviaCardPreview, TriviaCardContentBlok, MaximizeYourBenefitsPreview, MaximizeYourBenefitsContentBlok, SweepstakesPrizesPreview, SweepstakesPrizesContentBlok, ParticipatingStoresModalPreview, ParticipatingStoresModalContentBlok, RewardsCarouselPreview, RewardsCarouselContentBlok, AppDownloadPreview, AppDownloadContentBlok, TermsAndConditionsPreview, TermsAndConditionsContentBlok, HealthAdventurePreview, HealthAdventureContentBlok } from './CmsComponentPreview';

// Public Storyblok Preview token for the `sunny-dev` space (572723165993589).
// Preview tokens are read-only and safe to ship in browser JS.
// Override via REACT_APP_STORYBLOK_PREVIEW_TOKEN if you've issued a fresh one.
const FALLBACK_PREVIEW_TOKEN = 'UKoPrcuEdISxVx3Hmh5O1gtt';
const PREVIEW_TOKEN =
  (process.env.REACT_APP_STORYBLOK_PREVIEW_TOKEN || '').trim() || FALLBACK_PREVIEW_TOKEN;

// Init once at module load. storyblokInit is idempotent — won't double-init.
// region: 'us' is REQUIRED — our space (572723165993589) lives in the US
// region. Without this the SDK defaults to api.storyblok.com (EU) and every
// CDN read 401s because the token doesn't exist on that endpoint.
storyblokInit({
  accessToken: PREVIEW_TOKEN,
  use: [apiPlugin],
  apiOptions: {
    region: 'us',
  },
});

interface ParsedContext {
  tenantSlug?: string;
  contentType?: string;
  pageCode?: string;
  storySlug?: string;
}

function parseFullSlug(fullSlug: string | undefined): ParsedContext {
  if (!fullSlug) return {};
  const segs = fullSlug.split('/').filter(Boolean);
  // Expected shape: customer/sponsor/tenant/content-type/page-code/story-slug
  return {
    tenantSlug: segs[2],
    contentType: segs[3],
    pageCode: segs[4],
    storySlug: segs[5],
  };
}

export default function StoryblokPreview() {
  const location = useLocation();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const storyId = query.get('_storyblok') || '';
  const componentHint = query.get('_storyblok_c') || '';
  const lang = query.get('_storyblok_lang') || 'default';

  const [story, setStory] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  useEffect(() => {
    if (!storyId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    // `find_by` is a valid Storyblok CDN parameter but isn't on the SDK's
    // typed param list (v6 ISbStoriesParams omits it). Cast to bypass.
    const params = {
      find_by: 'id',
      version: 'draft',
      // content_page.categories + content_category.items → FAQ/Action/Agreement (categorised)
      // content_page.items → Cards/Agreements flat content (items live directly on the page)
      resolve_relations: 'content_page.categories,content_category.items,content_page.items',
      // cv busts the CDN cache so draft edits (including asset uploads) are
      // always fresh — without it the CDN may serve a stale snapshot.
      cv: Date.now(),
      ...(lang !== 'default' ? { language: lang } : {}),
    } as unknown as Parameters<ReturnType<typeof getStoryblokApi>['get']>[1];

    getStoryblokApi()
      .get(`cdn/stories/${storyId}`, params)
      .then((res: { data: { story: unknown } }) => {
        if (cancelled) return;
        setStory(res.data?.story ?? null);
      })
      .catch((err: { message?: string; response?: { data?: unknown } }) => {
        if (cancelled) return;
        setError(err.message || 'Storyblok CDN fetch failed');
        // eslint-disable-next-line no-console
        console.error('[storyblok-preview] fetch error', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [storyId, lang]);

  // useStoryblokState wires up the Storyblok Visual Editor bridge so the
  // preview updates in real-time when any field (including assets like image/
  // icon) is changed in the editor. Without this, the preview stays frozen
  // at the initial CDN snapshot and never reflects editor changes.
  const liveStory = useStoryblokState(story);
  const activeStory = liveStory ?? story;

  const parsed = useMemo(() => parseFullSlug(activeStory?.full_slug), [activeStory]);

  // If we have no story ID, this isn't an editor session — show the stub.
  if (!storyId) {
    return (
      <Box sx={{ p: 4, maxWidth: 960, mx: 'auto', fontFamily: 'system-ui, sans-serif' }}>
        <Alert severity="info">
          Open this page from inside Storyblok's Visual Editor — it expects a
          <code style={{ marginLeft: 4, marginRight: 4 }}>?_storyblok=&lt;story_id&gt;</code>
          query param.
        </Alert>
      </Box>
    );
  }

  // ?debug=1 keeps the raw JSON dump visible alongside the rendered preview.
  // Without it we render the real FAQ UI by default (matches mobile-app design).
  const debug = query.get('debug') === '1';

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1100, mx: 'auto', fontFamily: 'system-ui, sans-serif' }}>
      {/* Real rendering — always on unless ?debug=1 forces the JSON view. */}
      {activeStory && !debug && <RenderedStory story={activeStory} />}

      {!activeStory && !error && loading && (
        <Alert severity="info">Fetching story from Storyblok CDN…</Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>Fetch failed:</strong> {error}
          <Box sx={{ mt: 1, fontSize: 12, fontFamily: 'monospace' }}>
            Append <code>?debug=1</code> to the URL to see the raw dump.
          </Box>
        </Alert>
      )}

      {/* Debug surface — visible with ?debug=1 or when component dispatch
          has no renderer for this story type yet. */}
      {debug && activeStory && (
        <Box sx={{ mt: 4, pt: 4, borderTop: '1px dashed #ccc' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Debug mode — append <code>?debug=0</code> or remove the param to see rendered output.
          </Typography>
          <DebugDump
            storyId={storyId}
            componentHint={componentHint}
            lang={lang}
            story={activeStory}
            parsed={parsed}
          />
        </Box>
      )}
    </Box>
  );
}

// ── Component dispatcher ──────────────────────────────────────────────

interface RenderedStoryProps {
  story: {
    name?: string;
    full_slug?: string;
    content?: { component?: string; [k: string]: unknown };
  };
  rewardAmount?: string | null;
}

function RenderedStory({ story, rewardAmount }: RenderedStoryProps) {
  const component = story.content?.component;
  const fullSlug = story.full_slug || '';
  const contentTypeFromSlug = fullSlug.split('/')[3];

  // Page / category containers — content_page / content_category are reused
  // across content types, so dispatch by the contentType segment in the slug.
  if (component === 'content_page') {
    if (contentTypeFromSlug === 'action') {
      return <ActionPagePreview pageBlok={story.content as unknown as Parameters<typeof ActionPagePreview>[0]['pageBlok']} rewardAmount={rewardAmount} />;
    }
    if (contentTypeFromSlug === 'card') {
      return <CardPagePreview pageBlok={story.content as unknown as Parameters<typeof CardPagePreview>[0]['pageBlok']} />;
    }
    if (contentTypeFromSlug === 'agreement') {
      return <AgreementPagePreview pageBlok={story.content as unknown as Parameters<typeof AgreementPagePreview>[0]['pageBlok']} />;
    }
    // default → FAQ
    return <FaqPagePreview pageBlok={story.content as unknown as Parameters<typeof FaqPagePreview>[0]['pageBlok']} />;
  }

  if (component === 'content_category') {
    if (contentTypeFromSlug === 'action') {
      return <ActionCategoryPreview categoryBlok={story.content as unknown as Parameters<typeof ActionCategoryPreview>[0]['categoryBlok']} rewardAmount={rewardAmount} />;
    }
    if (contentTypeFromSlug === 'card') {
      return <CardCategoryPreview categoryBlok={story.content as unknown as Parameters<typeof CardCategoryPreview>[0]['categoryBlok']} />;
    }
    if (contentTypeFromSlug === 'agreement') {
      return <AgreementCategoryPreview categoryBlok={story.content as unknown as Parameters<typeof AgreementCategoryPreview>[0]['categoryBlok']} />;
    }
    // default → FAQ
    return <FaqCategoryPreview categoryBlok={story.content as unknown as Parameters<typeof FaqCategoryPreview>[0]['categoryBlok']} storyName={story.name} />;
  }

  // Item-level components
  switch (component) {
    case 'faq_question':
      return <FaqQuestionPreview questionBlok={story.content as unknown as Parameters<typeof FaqQuestionPreview>[0]['questionBlok']} />;
    case 'content_card':
      return <ContentCardPreview cardBlok={story.content as unknown as Parameters<typeof ContentCardPreview>[0]['cardBlok']} />;
    case 'action_item':
      return <ActionItemPreview itemBlok={story.content as unknown as Parameters<typeof ActionItemPreview>[0]['itemBlok']} rewardAmount={rewardAmount} />;
    case 'agreement_item':
      return <AgreementItemPreview itemBlok={story.content as unknown as Parameters<typeof AgreementItemPreview>[0]['itemBlok']} />;
    // CMS component types
    case 'tool_tip_content':
      return <ToolTipPreview blok={story.content as unknown as ToolTipContentBlok} />;
    case 'information_modal_content':
      return <InformationModalPreview blok={story.content as unknown as InformationModalContentBlok} />;
    case 'benefits_card_art_content':
      return <BenefitsCardArtPreview blok={story.content as unknown as BenefitsCardArtContentBlok} />;
    case 'cta_card_banner_content':
      return <CtaCardBannerPreview blok={story.content as unknown as CtaCardBannerContentBlok} />;
    case 'shop_online_carousel_content':
      return <ShopOnlineCarouselPreview blok={story.content as unknown as ShopOnlineCarouselContentBlok} />;
    case 'onboarding_splash_content':
      return <OnboardingSplashPreview blok={story.content as unknown as OnboardingSplashContentBlok} />;
    case 'purse_details_content':
      return <PurseDetailsPreview blok={story.content as unknown as PurseDetailsContentBlok} />;
    case 'cohort_specific_content':
      return <CohortSpecificContentPreview blok={story.content as unknown as CohortSpecificContentBlok} />;
    case 'ui_language_resource_content':
      return <UILanguageResourcePreview blok={story.content as unknown as UILanguageResourceContentBlok} />;
    case 'trivia_card_content':
      return <TriviaCardPreview blok={story.content as unknown as TriviaCardContentBlok} />;
    case 'maximize_your_benefits_content':
      return <MaximizeYourBenefitsPreview blok={story.content as unknown as MaximizeYourBenefitsContentBlok} />;
    case 'sweepstakes_prizes_content':
      return <SweepstakesPrizesPreview blok={story.content as unknown as SweepstakesPrizesContentBlok} />;
    case 'participating_stores_modal_content':
      return <ParticipatingStoresModalPreview blok={story.content as unknown as ParticipatingStoresModalContentBlok} />;
    case 'rewards_carousel_content':
      return <RewardsCarouselPreview blok={story.content as unknown as RewardsCarouselContentBlok} />;
    case 'app_download_content':
      return <AppDownloadPreview blok={story.content as unknown as AppDownloadContentBlok} />;
    case 'terms_and_conditions_content':
    case 'card_terms_and_conditions_content':
      return <TermsAndConditionsPreview blok={story.content as unknown as TermsAndConditionsContentBlok} />;
    // Task V2 completion modal. Each field block is click-to-select, so an
    // author editing one of two attestations can see which sentence is which.
    case 'taskv2_completion_ui':
      return <CompletionUiPreview blok={story.content as unknown as CompletionUiBlok} />;
    case 'health_adventure_content':
      return <HealthAdventurePreview blok={story.content as unknown as HealthAdventureContentBlok} />;
    case 'card_item':
      return (
        <Alert severity="info">
          Renderer for <strong>{component}</strong> isn't built yet.
          Append <code>?debug=1</code> to the URL to see the raw story data.
        </Alert>
      );
    default:
      return (
        <Alert severity="warning">
          Unknown component <strong>{component || '(missing)'}</strong>. Append{' '}
          <code>?debug=1</code> to inspect the raw payload.
        </Alert>
      );
  }
}

// ── Debug dump (the old raw-fetch UI, behind ?debug=1) ────────────────

interface DebugDumpProps {
  storyId: string;
  componentHint: string;
  lang: string;
  story: {
    name?: string;
    full_slug?: string;
    uuid?: string;
    content?: { component?: string; [k: string]: unknown };
    rels?: unknown[];
  };
  parsed: ParsedContext;
}

function DebugDump({ storyId, componentHint, lang, story, parsed }: DebugDumpProps) {
  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1 }}>
        Storyblok preview — raw fetch
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
        Story fetched live from Storyblok CDN by ID. Real FAQ rendering replaces this dump next.
      </Typography>

      <Stack spacing={3}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="overline">Editor session</Typography>
          <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 1 }}>
            <Typography sx={{ fontWeight: 600 }}>story_id</Typography>
            <Typography sx={{ fontFamily: 'monospace' }}>{storyId}</Typography>
            <Typography sx={{ fontWeight: 600 }}>component</Typography>
            <Typography sx={{ fontFamily: 'monospace' }}>{componentHint || '—'}</Typography>
            <Typography sx={{ fontWeight: 600 }}>language</Typography>
            <Typography sx={{ fontFamily: 'monospace' }}>{lang}</Typography>
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="overline">Story metadata</Typography>
              <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 1 }}>
                <Typography sx={{ fontWeight: 600 }}>name</Typography>
                <Typography sx={{ fontFamily: 'monospace' }}>{story.name}</Typography>
                <Typography sx={{ fontWeight: 600 }}>full_slug</Typography>
                <Typography sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {story.full_slug}
                </Typography>
                <Typography sx={{ fontWeight: 600 }}>uuid</Typography>
                <Typography sx={{ fontFamily: 'monospace' }}>{story.uuid}</Typography>
                <Typography sx={{ fontWeight: 600 }}>component</Typography>
                <Typography sx={{ fontFamily: 'monospace' }}>{story.content?.component}</Typography>
              </Box>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="overline">Parsed from full_slug</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                <Chip label={`tenant: ${parsed.tenantSlug || '—'}`} color="primary" size="small" />
                <Chip label={`content_type: ${parsed.contentType || '—'}`} color="secondary" size="small" />
                <Chip label={`page_code: ${parsed.pageCode || '—'}`} size="small" />
                <Chip label={`story: ${parsed.storySlug || '—'}`} size="small" />
              </Stack>
              {!parsed.contentType && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Couldn't derive content type — story is not nested 6 levels deep. Check the
                  folder hierarchy (expected: customer/sponsor/tenant/content-type/page-code/story).
                </Alert>
              )}
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="overline">story.content (JSON)</Typography>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  fontSize: 12,
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  maxHeight: 480,
                  overflow: 'auto',
                  background: '#0d1117',
                  color: '#e6edf3',
                  p: 2,
                  borderRadius: 1,
                }}
              >
                {JSON.stringify(story.content, null, 2)}
              </Box>
            </Paper>

            {Array.isArray((story as { rels?: unknown[] }).rels) && (story as { rels: unknown[] }).rels.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="overline">
                  rels (resolved relations — {(story as { rels: unknown[] }).rels.length} entries)
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    fontSize: 11,
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                    maxHeight: 360,
                    overflow: 'auto',
                    background: '#0d1117',
                    color: '#e6edf3',
                    p: 2,
                    borderRadius: 1,
                  }}
                >
                  {JSON.stringify((story as { rels: unknown[] }).rels, null, 2)}
                </Box>
              </Paper>
            )}
      </Stack>
    </Box>
  );
}
